import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BillingFields, emptyBilling, billingValid } from "@/components/BillingFields";
import { SepaFields, emptySepa, sepaValid } from "@/components/SepaFields";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lock, AlertTriangle, LogOut, CheckCircle2, CreditCard, Landmark } from "lucide-react";

const fmt = (n) => Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Checkout() {
  const { user, authHeaders, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [billing, setBilling] = useState(emptyBilling);
  const [loading, setLoading] = useState(false);
  const [payMethod, setPayMethod] = useState("auto");
  const [sepa, setSepa] = useState(emptySepa);
  const [ddSent, setDdSent] = useState(null);
  const isReturning = !!user?.practice?.first_paid_at;

  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
    // Nur bereits zahlende Abonnenten zurück ins Dashboard – Trial-Nutzer dürfen früh upgraden.
    if (user && user.access === "active" && user.practice?.subscription_status === "active") navigate("/admin", { replace: true });
    if (user && user.email) setBilling((b) => ({ ...b, email: user.email }));
  }, [user, navigate]);

  useEffect(() => { api.get("/payments/plan").then(({ data }) => setPlan(data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!(isReturning && payMethod === "directdebit") && !billingValid(billing)) return toast.error("Bitte alle Rechnungsfelder vollständig ausfüllen.");
    if (payMethod === "directdebit" && !sepaValid(sepa)) return toast.error("Bitte Kontoinhaber, gültige IBAN und Mandat-Bestätigung angeben.");
    setLoading(true);
    try {
      if (payMethod === "directdebit") {
        // Kein Mollie-Redirect: Mandat wird direkt erteilt, Einzug läuft im Hintergrund (Bankarbeitstage).
        const { data } = isReturning
          ? await api.post("/admin/billing/sepa", sepa, { headers: authHeaders })
          : await api.post("/payments/checkout", { billing_address: billing, payment_method: "directdebit", sepa }, { headers: authHeaders });
        setDdSent(data);
        setLoading(false);
        return;
      }
      const { data } = await api.post("/payments/checkout", { billing_address: billing, payment_method: payMethod }, { headers: authHeaders });
      localStorage.setItem("pending_payment_id", data.payment_id);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout fehlgeschlagen.");
      setLoading(false);
    }
  };

  if (!user || user === false) return null;

  if (ddSent) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center px-5 py-10" data-testid="checkout-sepa-confirmation">
        <div className="w-full max-w-md bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mb-5">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-emerald-900 mb-3">Lastschriftmandat erteilt</h1>
          <p className="text-stone-600 leading-relaxed">
            Vielen Dank! Der Betrag wird per <strong>SEPA-Lastschrift</strong> von Ihrem Konto eingezogen, danach automatisch alle 4 Wochen.
            Ihr Zugang ist ab sofort freigeschaltet.
          </p>
          <p className="text-xs text-stone-400 mt-4">Der Einzug kann einige Bankarbeitstage dauern. Ihre Rechnung erhalten Sie nach Zahlungseingang per E-Mail.</p>
          <Button data-testid="sepa-back-dashboard" onClick={async () => { await refresh(); navigate("/admin"); }}
            className="mt-6 w-full h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
            Zurück zum Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 grid place-items-center px-5 py-10" data-testid="checkout-page">
      <div className="w-full max-w-3xl">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">
            {user.practice?.subscription_status === "cancelled"
              ? "Ihr Abo wurde gekündigt. Buchen Sie erneut, um den vollen Zugang inkl. QR-Code wieder freizuschalten."
              : (isReturning && user.access !== "active")
                ? "Ihr bezahlter Zeitraum ist abgelaufen. Bitte begleichen Sie den fälligen Beitrag – am einfachsten per SEPA-Lastschrift. Ihr QR-Code wird danach wieder aktiv."
              : (user.access === "active" && user.practice?.trial_ends_at)
                ? "Sie nutzen LAIzyDocu aktuell im Testzeitraum. Schließen Sie jetzt Ihr Abo ab, um dauerhaft Zugang zu behalten – Ihr Testzeitraum läuft bis zur ersten Zahlung normal weiter."
                : user.access === "active"
                  ? "Sie nutzen LAIzyDocu derzeit ohne aktives Abo. Schließen Sie jetzt Ihr Abo ab, um den vollen Zugang dauerhaft zu sichern."
                  : "Ihr Testzeitraum ist abgelaufen. Bitte schließen Sie die Buchung ab, um LAIzyDocu weiter zu nutzen – Ihr QR-Code wird danach wieder aktiv."}
          </p>
        </div>

        <button type="button" onClick={() => navigate("/admin")} data-testid="checkout-back"
          className="mb-4 text-sm text-stone-500 hover:text-emerald-800">← Zurück zum Dashboard</button>

        <div className="grid md:grid-cols-[1.2fr_1fr] gap-6 items-start">
          <form onSubmit={submit} className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-7 md:p-8 space-y-6">
            <h1 className="font-display text-2xl font-semibold text-emerald-900">Jetzt buchen</h1>

            <div>
              <p className="text-sm font-semibold text-stone-800 mb-1">Wie möchten Sie zahlen?</p>
              <p className="text-xs text-stone-500 mb-4">Wählen Sie Ihre bevorzugte Zahlungsart – auch per SEPA-Lastschrift von jeder Bank.</p>
              <div className="grid gap-3">
                <button type="button" data-testid="pay-method-auto" onClick={() => setPayMethod("auto")}
                  className={`text-left rounded-2xl border-2 p-4 flex items-start gap-3 transition-colors ${payMethod === "auto" ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-600/20" : "border-stone-200 hover:border-stone-300"}`}>
                  <span className={`mt-0.5 w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 ${payMethod === "auto" ? "border-emerald-600" : "border-stone-300"}`}>
                    {payMethod === "auto" && <span className="w-2.5 h-2.5 rounded-full bg-emerald-600" />}
                  </span>
                  <CreditCard className={`w-5 h-5 shrink-0 mt-0.5 ${payMethod === "auto" ? "text-emerald-700" : "text-stone-400"}`} />
                  <span className="flex-1">
                    <span className="block font-semibold text-emerald-900">Karte / Sofortüberweisung</span>
                    <span className="block text-xs text-stone-500 mt-0.5">Sofort freigeschaltet · automatisches Monatsabo</span>
                  </span>
                </button>
                <button type="button" data-testid="pay-method-directdebit" onClick={() => setPayMethod("directdebit")}
                  className={`text-left rounded-2xl border-2 p-4 flex items-start gap-3 transition-colors ${payMethod === "directdebit" ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-600/20" : "border-stone-200 hover:border-stone-300"}`}>
                  <span className={`mt-0.5 w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 ${payMethod === "directdebit" ? "border-emerald-600" : "border-stone-300"}`}>
                    {payMethod === "directdebit" && <span className="w-2.5 h-2.5 rounded-full bg-emerald-600" />}
                  </span>
                  <Landmark className={`w-5 h-5 shrink-0 mt-0.5 ${payMethod === "directdebit" ? "text-emerald-700" : "text-stone-400"}`} />
                  <span className="flex-1">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-emerald-900">SEPA-Lastschrift</span>
                      <span className="text-[11px] font-medium bg-emerald-600 text-white rounded-full px-2 py-0.5">Für jede Bank – auch apoBank</span>
                    </span>
                    <span className="block text-xs text-stone-500 mt-0.5">Automatischer Einzug alle 4 Wochen – keine Rechnungen überweisen.</span>
                  </span>
                </button>
              </div>
            </div>

            {payMethod === "directdebit" && (
              <div>
                <p className="text-sm font-semibold text-stone-800 mb-4">Bankverbindung</p>
                <SepaFields value={sepa} onChange={setSepa} amount={plan ? fmt(plan.monthly_gross) : "69,99"}
                  firstAmount={!isReturning && plan ? fmt(plan.first_payment) : null} />
              </div>
            )}
            {!(isReturning && payMethod === "directdebit") && (
              <div>
                <p className="text-sm font-semibold text-stone-800 mb-4">Rechnungsadresse</p>
                <BillingFields value={billing} onChange={setBilling} />
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="checkout-submit"
              className="w-full h-14 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white text-base font-medium">
              {loading
                ? <><Loader2 className="mr-2 w-5 h-5 animate-spin" /> {payMethod === "directdebit" ? "Mandat wird erteilt…" : "Weiterleitung…"}</>
                : <><Lock className="mr-2 w-4 h-4" /> {payMethod === "directdebit" ? "Mandat erteilen & zahlungspflichtig buchen" : "Zahlungspflichtig buchen"}</>}
            </Button>
            <button type="button" onClick={logout} data-testid="checkout-logout"
              className="w-full text-sm text-stone-400 hover:text-stone-600 flex items-center justify-center gap-1.5">
              <LogOut className="w-4 h-4" /> Abmelden
            </button>
          </form>

          {plan && (
            <div className="bg-emerald-900 text-white rounded-3xl p-7" data-testid="plan-summary">
              <p className="text-emerald-300 text-sm font-medium">LAIzyDocu Pro</p>
              <div className="mt-3 flex items-end gap-2">
                <span className="font-display text-4xl font-semibold">{fmt(plan.monthly_gross)} €</span>
                <span className="text-emerald-200 mb-1">/ Monat</span>
              </div>
              <div className="mt-6 pt-5 border-t border-emerald-700/60 text-sm space-y-1.5">
                {isReturning && payMethod === "directdebit" ? (
                  <div className="flex justify-between gap-3 font-semibold text-base mt-2"><span>Fälliger Beitrag (4 Wochen)</span><span className="whitespace-nowrap" data-testid="first-payment-amount">{fmt(plan.monthly_gross)} €</span></div>
                ) : (
                  <>
                    <div className="flex justify-between"><span className="text-emerald-200">Einmalige Einrichtung</span><span>{fmt(plan.setup_net)} € netto</span></div>
                    <div className="flex justify-between"><span className="text-emerald-200">1. Monat</span><span>{fmt(plan.monthly_gross)} €</span></div>
                    <div className="flex justify-between font-semibold text-base mt-2"><span>Heute fällig</span><span data-testid="first-payment-amount">{fmt(plan.first_payment)} €</span></div>
                  </>
                )}
                <p className="text-xs text-emerald-300/80 pt-1">
                  {payMethod === "directdebit"
                    ? <>Danach automatisch alle 4 Wochen {fmt(plan.monthly_gross)} € per Lastschrift. Jederzeit kündbar.</>
                    : <>Danach automatisch {fmt(plan.monthly_gross)} € / Monat. Jederzeit kündbar.</>}
                </p>
              </div>
              <div className="mt-5 flex items-center gap-2 text-xs text-emerald-200"><CheckCircle2 className="w-4 h-4" /> Sichere Zahlung über Mollie</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
